import * as React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ValidatorForm } from 'react-material-ui-form-validator';
import CustomButton from './CustomButton';

// Modal shell mirroring ClientApp2 (DialogWrapper / ConfirmDialogWrapper): the title bar renders
// only when a title is set (`{title && <DialogTitle>}`) — no close X — with cancel/confirm actions.
// Title matches CA2's MuiDialogTitle override (fontSize 1.25rem, default weight); actions right-aligned.
const useStyles = makeStyles(() => ({
  title: { padding: '16px 24px' },
  titleText: { fontSize: '1.25rem' },
  content: { padding: '8px 24px 20px', overflowY: 'visible' },
  actions: { gap: 8, padding: '0 24px 16px', justifyContent: 'flex-end' }
}));

export interface CustomDialogProps {
  open: boolean;
  title: React.ReactNode;
  onClose: () => void;
  maxWidth?: 'xs' | 'sm' | 'md';
  children: React.ReactNode;
  /** When set, the body is wrapped in a ValidatorForm and the confirm button submits it. */
  onSubmit?: () => void;
  /** Plain confirm handler (used when there is no form to submit). */
  onConfirm?: () => void;
  cancelLabel: string;
  confirmLabel: string;
  confirmDisabled?: boolean;
  /** Shows the uniform spinner on the confirm button and disables both actions while in flight. */
  isLoading?: boolean;
  /** Hide the actions row entirely (dialog manages its own footer). */
  hideActions?: boolean;
}

/** Shared modal shell (idem ClientApp2): optional title + body + cancel/confirm actions. Reusable. */
const CustomDialog: React.FC<CustomDialogProps> = ({
  open,
  title,
  onClose,
  maxWidth = 'sm',
  children,
  onSubmit,
  onConfirm,
  cancelLabel,
  confirmLabel,
  confirmDisabled,
  isLoading,
  hideActions
}) => {
  const c = useStyles();

  const body = (
    <>
      <DialogContent className={c.content}>{children}</DialogContent>
      {!hideActions && (
        <DialogActions className={c.actions}>
          <CustomButton variant="outlined" disabled={isLoading} onClick={onClose}>
            {cancelLabel}
          </CustomButton>
          <CustomButton
            type={onSubmit ? 'submit' : 'button'}
            disabled={confirmDisabled}
            isLoading={isLoading}
            onClick={onSubmit ? undefined : onConfirm}
          >
            {confirmLabel}
          </CustomButton>
        </DialogActions>
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth>
      {title ? (
        <DialogTitle disableTypography className={c.title}>
          <Typography variant="h6" className={c.titleText}>
            {title}
          </Typography>
        </DialogTitle>
      ) : null}
      {onSubmit ? <ValidatorForm onSubmit={onSubmit}>{body}</ValidatorForm> : body}
    </Dialog>
  );
};

export default CustomDialog;
